import { isEmpty } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { PvrAsset } from '../../../../../components/Assets/PvrAsset';
import { AtsAsset } from '../../../../../components/ATS/ats-asset';
import { Swimlane } from '../../../../../components/Swimlane/Swimlane';
import { routes } from '../../../../../config';
import { RootState } from '../../../../../interfaces';
import { useSectionItems } from '../../../../../queries/ats/layout/queries';
import { Layout } from '../../../../../queries/ats/layout/types';
import { useGroupedRecordings } from '../../../../../queries/recordings/queries';
import { Recording } from '../../../../../queries/recordings/types';
import { generateSwimlane } from '../../../../../queries/recordings/utils';
import { useUnmanagedRoutePath } from '../../../../../routes/unmanaged-routes/use-unmanaged-route-path';
import { nonNullable } from '../../../../../utils/commonUtils';
import { PatternHandler } from '../../../PatternHandler';

interface SectionMyRecordingsProps {
  view: Layout.SectionView;
  data: Layout.SectionItem[];
  max?: number;
  className?: string;
}

export function SectionMyRecordings({ view, data, max = 40, className }: SectionMyRecordingsProps) {
  const loggenInWithIp = useSelector((state: RootState) => state.authReducer.loggedInWithIp);
  const loggenInWithCredentials = useSelector((state: RootState) => state.authReducer.loggedInWithCredentials);

  const { data: swimlane } = useGroupedRecordings(
    (items) => generateSwimlane(Recording.Swimlane.Titles.AllRecordings, items),
    Boolean(loggenInWithIp || loggenInWithCredentials),
  );

  const { data: items } = useSectionItems({ link: view.link });

  const path = useUnmanagedRoutePath('my-recordings');

  if (!swimlane || !swimlane.groups || !path) {
    return null;
  }

  const groups = swimlane.groups.filter(nonNullable).slice(0, max);

  if (isEmpty(groups)) {
    return null;
  }

  return (
    <Swimlane className={className}>
      <Swimlane.Header>
        <Swimlane.Title to={routes.unmanaged.base + routes.pvr.base}>{view.label}</Swimlane.Title>
        <Swimlane.Buttons />
      </Swimlane.Header>
      <Swimlane.Slider>
        {groups.map((group) => (
          <PvrAsset key={group.pvrId} group={group} />
        ))}
        {items?.map((item) => (
          <PatternHandler key={item.id} pattern={view.style.pattern} item={item} />
        ))}
      </Swimlane.Slider>
    </Swimlane>
  );
}

export function SectionMyRecordingsSkeleton({ view, className }: Pick<SectionMyRecordingsProps, 'view' | 'className'>) {
  return (
    <Swimlane className={className}>
      <Swimlane.Header>
        <Swimlane.Title>{view.label}</Swimlane.Title>
        <Swimlane.Buttons />
      </Swimlane.Header>
      <Swimlane.Slider>
        {Array.from({ length: 5 }).map((_, idx) => (
          <AtsAsset.Skeleton key={idx} pattern={view.style.pattern} />
        ))}
      </Swimlane.Slider>
    </Swimlane>
  );
}

SectionMyRecordings.Skeleton = SectionMyRecordingsSkeleton;
